import { useState } from 'react';
import { currentHotelFilterSelectionsVar, useBackgroundFlightSearch, useSearch, useFlightFromTrip, currentTripStateIdVar, HotelAvailabilityErrors, } from '@honeycomb/data';
import { useLocalStorage } from '../../utils';
import { validateFlightInTrip, validateFlightInLocalStorage } from './utils';
import { HotelBasketActionType, useHotelBasketContext } from '../hotel/HotelBasketProvider';
/**
 *
 * This hook does the following
 * Check whether we need a background flight search and we have an available room (to wait for availability call to finish)
 * If we do, either use local storage or fire a flight search, and call the side effect
 * For local storage, check whether we have a linked tripId > use the tripId and call useFlightFromTrip > call side effect
 * For flight search, fire a background flight search > store the response in local storage > call the side effect
 */
export function useBackgroundFlight(staticDetails, requiresBackgroundFlightSearch, validFlightInContext, hotelCheckInDate, departureFlightCutOff, sideEffect, errorSideEffect) {
    var _a, _b;
    const { estabId, linkedAirports } = staticDetails;
    const [initialSearchState] = useSearch([]);
    const filters = currentHotelFilterSelectionsVar();
    const tripStateId = (_a = currentTripStateIdVar()) !== null && _a !== void 0 ? _a : '';
    const [linkedTrips, setLinkedTrips] = useLocalStorage('linkedTrips');
    const [skipFlightFromTrip, setSkipFlightFromTrip] = useState(true);
    const { dispatch, hotelBasket: { selectedRooms, waitingForRooms, waitingForFlights, tripStateId: contextTripState, availabilityErrors, }, } = useHotelBasketContext();
    const hasRoomsAvailable = !waitingForRooms && (selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms.length) > 0;
    const flightFromTripSideEffect = (data) => {
        var _a;
        if (validateFlightInTrip(data, linkedAirports, hotelCheckInDate, departureFlightCutOff)) {
            const { tripState, flight, requestTripStateId } = (_a = data === null || data === void 0 ? void 0 : data.getFlightFromTrip) !== null && _a !== void 0 ? _a : {};
            const flightFromTrip = {
                backgroundFlightSearch: {
                    requestTripStateId: requestTripStateId !== null && requestTripStateId !== void 0 ? requestTripStateId : '',
                    tripState,
                    flight,
                },
            };
            sideEffect(flightFromTrip);
        }
        else {
            // if linked airport does not match then clear storage, so that it will trigger another search
            // Not sure we want to clear the storage as the user may want to select another hotel from the SRP with the same airport
            // We will want to fire another search though
            setLinkedTrips(Object.assign(Object.assign({}, (linkedTrips !== null && linkedTrips !== void 0 ? linkedTrips : {})), { [tripStateId]: null }));
        }
    };
    useFlightFromTrip((_b = linkedTrips === null || linkedTrips === void 0 ? void 0 : linkedTrips[initialSearchState.tripStateId]) === null || _b === void 0 ? void 0 : _b.linkedTripId, skipFlightFromTrip, [flightFromTripSideEffect], [errorSideEffect]);
    const setLinkedTripsInLocalStorage = (data) => {
        var _a, _b, _c;
        const linkedTrip = {
            originalTripId: initialSearchState.tripStateId,
            linkedTripId: (_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.backgroundFlightSearch) === null || _a === void 0 ? void 0 : _a.tripState) === null || _b === void 0 ? void 0 : _b.tripStateId) !== null && _c !== void 0 ? _c : '',
        };
        setLinkedTrips(Object.assign(Object.assign({}, (linkedTrips !== null && linkedTrips !== void 0 ? linkedTrips : {})), { [tripStateId]: linkedTrip, [linkedTrip.linkedTripId]: linkedTrip }));
    };
    const [mutation, data] = useBackgroundFlightSearch([sideEffect, setLinkedTripsInLocalStorage, () => setSkipFlightFromTrip(true)], [errorSideEffect]);
    const validFlightInLocalStorage = validateFlightInLocalStorage(linkedTrips, tripStateId);
    if (hotelCheckInDate &&
        requiresBackgroundFlightSearch &&
        validFlightInLocalStorage &&
        !validFlightInContext &&
        skipFlightFromTrip) {
        setSkipFlightFromTrip(false);
    }
    const triggerBackgroundFlightSearch = () => {
        var _a, _b;
        mutation({
            estabId: parseInt(estabId),
            tripStateId,
            boardTypeFilter: (_b = (_a = filters.checkboxFilters) === null || _a === void 0 ? void 0 : _a.find((f) => f.id === 'boardTypes')) === null || _b === void 0 ? void 0 : _b.selectedIds.map(Number),
        });
    };
    const hasBgFlightError = availabilityErrors === null || availabilityErrors === void 0 ? void 0 : availabilityErrors.includes(HotelAvailabilityErrors.FLIGHT_ERROR);
    if (requiresBackgroundFlightSearch &&
        !validFlightInLocalStorage &&
        tripStateId &&
        !data.loading &&
        !hasBgFlightError &&
        hasRoomsAvailable &&
        !contextTripState) {
        triggerBackgroundFlightSearch();
    }
    // If we dont have any rooms available, we wont be doing a flight search, so set the waiting to false if that was true
    if (!waitingForRooms && !(selectedRooms === null || selectedRooms === void 0 ? void 0 : selectedRooms.length) && waitingForFlights) {
        dispatch({
            type: HotelBasketActionType.SET_WAITING_FOR_FLIGHT,
            payload: false,
        });
    }
    return {
        triggerBackgroundFlightSearch,
    };
}
